import formatCurrency from '../../../../../utils/formatCurrency';
import { getPrimaryPhaseMetricLabel, ORDER_PRIMARY_PHASE_METRICS } from '../../pipeline/lib/orderPrimaryPhaseMetric';
import { buildCompareLineLegend, resolveTrendChartGridTop } from '../../trend/lib/trendChartOptionShared';

export const CHART_SUMMARY_SALES_LEGEND_LABEL = '销售总额';
export const CHART_SUMMARY_ORDER_LEGEND_LABEL = '订单数';

const METRIC_COUNT_LABEL = getPrimaryPhaseMetricLabel(ORDER_PRIMARY_PHASE_METRICS.COUNT);
const METRIC_TOTAL_PRICE_LABEL = getPrimaryPhaseMetricLabel(ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE);

const normalizeSummaryValues = (value) => {
	if (Array.isArray(value)) return value.map((item) => Number(item) || 0);
	return [Number(value) || 0];
};

export const formatChartSummarySalesText = (value) => formatCurrency(Number(value) || 0);
export const formatChartSummaryOrderCountText = (value) => `${Number(value) || 0}单`;

export const formatChartSummarySalesLegendLabel = (value) =>
	`${CHART_SUMMARY_SALES_LEGEND_LABEL} ${formatChartSummarySalesText(value)}`;

export const formatChartSummaryOrderLegendLabel = (value) =>
	`${CHART_SUMMARY_ORDER_LEGEND_LABEL} ${formatChartSummaryOrderCountText(value)}`;

export const buildMetricSummarySuffixByName = (salesTotal, orderCount) => ({
	[METRIC_TOTAL_PRICE_LABEL]: formatChartSummarySalesText(salesTotal),
	[METRIC_COUNT_LABEL]: formatChartSummaryOrderCountText(orderCount),
});

export const buildPeriodSummarySuffixByName = (periodNames = [], salesTotals = [], orderCounts = []) => {
	const suffixByName = {};
	for (let index = 0; index < periodNames.length; index += 1) {
		const name = String(periodNames[index] ?? '').trim();
		if (!name) continue;
		const suffix = `${formatChartSummarySalesText(salesTotals[index])} · ${formatChartSummaryOrderCountText(orderCounts[index])}`;
		suffixByName[name] = suffix;
		suffixByName[`${name}（基准）`] = suffix;
	}
	return suffixByName;
};

export const buildStandaloneSummaryLegend = (chartTheme, salesTotal, orderCount) => {
	const data = [formatChartSummarySalesLegendLabel(salesTotal), formatChartSummaryOrderLegendLabel(orderCount)];
	return {
		...buildCompareLineLegend(chartTheme, { data, itemCount: data.length }),
		selectedMode: false,
		icon: 'none',
	};
};

export const buildComparePeriodSummaryLegend = (chartTheme, compareLabels = [], salesTotals = [], orderCounts = []) => {
	const data = compareLabels.map(
		(label, index) =>
			`${String(label ?? '').trim()} ${formatChartSummarySalesText(salesTotals[index])} · ${formatChartSummaryOrderCountText(orderCounts[index])}`,
	);
	return {
		...buildCompareLineLegend(chartTheme, { data, itemCount: data.length }),
		selectedMode: false,
		icon: 'none',
	};
};

const resolveLegendSeriesNames = (option) => {
	const legendData = option?.legend?.data;
	if (Array.isArray(legendData) && legendData.length) return legendData;
	return (option?.series ?? []).map((item) => item.name).filter(Boolean);
};

const legendUsesMetricNames = (legendData = []) =>
	legendData.some((name) => name === METRIC_COUNT_LABEL || name === METRIC_TOTAL_PRICE_LABEL);

const legendUsesPeriodNames = (legendData = [], compareLabels = []) => {
	if (!Array.isArray(compareLabels) || compareLabels.length < 2) return false;
	return legendData.some((name) => {
		const base = String(name).replace(/（基准）$/, '');
		return compareLabels.includes(base) || compareLabels.includes(String(name));
	});
};

/** 「2024年 · 合同金额」：合同只挂金额总数；「2024年 · 单数」：只挂单数总数 */
const buildCompositePeriodSummarySuffixByName = (legendData = [], compareLabels = [], salesTotals = [], orderCounts = []) => {
	if (!Array.isArray(compareLabels) || compareLabels.length < 2) return null;
	const suffixByName = {};
	for (const rawName of legendData) {
		const name = String(rawName ?? '').trim();
		if (!name) continue;
		const labelIndex = compareLabels.findIndex(
			(label) => name === label || name.startsWith(`${label} ·`) || name.startsWith(`${label}（`),
		);
		if (labelIndex < 0) continue;

		const withoutBaseline = name.replace(/（基准）$/, '');
		let suffix = null;
		if (withoutBaseline.includes(` · ${METRIC_TOTAL_PRICE_LABEL}`) || withoutBaseline.endsWith(METRIC_TOTAL_PRICE_LABEL)) {
			suffix = formatChartSummarySalesText(salesTotals[labelIndex]);
		} else if (withoutBaseline.includes(` · ${METRIC_COUNT_LABEL}`) || withoutBaseline.endsWith(METRIC_COUNT_LABEL)) {
			suffix = formatChartSummaryOrderCountText(orderCounts[labelIndex]);
		}
		if (!suffix) continue;

		suffixByName[name] = suffix;
		if (withoutBaseline !== name) suffixByName[withoutBaseline] = suffix;
	}
	return Object.keys(suffixByName).length ? suffixByName : null;
};

const mergeLegendConfig = (existingLegend, nextLegend) => ({
	...(existingLegend ?? {}),
	...nextLegend,
	data: nextLegend.data ?? existingLegend?.data,
});

/** 将销售总额 / 订单数写入图例；无系列图例时追加独立汇总图例项 */
export const applyChartSummaryLegend = (option, chartSummary, chartTheme) => {
	if (!option || !chartTheme || !chartSummary) return option;

	const salesValues = normalizeSummaryValues(chartSummary.salesTotal);
	const orderValues = normalizeSummaryValues(chartSummary.orderCount);
	const legendData = resolveLegendSeriesNames(option);
	const existingLegend = option.legend ?? {};
	const hasVisibleLegend = existingLegend.show !== false && legendData.length > 0;
	const hasCompositeSeriesName = legendData.some((name) => String(name).includes(' · '));

	if (hasVisibleLegend && !hasCompositeSeriesName) {
		let suffixByName = null;
		if (legendUsesPeriodNames(legendData, chartSummary.compareLabels)) {
			suffixByName = buildPeriodSummarySuffixByName(chartSummary.compareLabels, salesValues, orderValues);
		} else if (legendUsesMetricNames(legendData)) {
			suffixByName = buildMetricSummarySuffixByName(salesValues[0], orderValues[0]);
		}

		if (suffixByName && Object.keys(suffixByName).length) {
			const legend = mergeLegendConfig(
				existingLegend,
				buildCompareLineLegend(chartTheme, {
					data: existingLegend.data ?? legendData,
					itemCount: legendData.length,
					suffixByName,
				}),
			);
			return {
				...option,
				legend,
				grid: {
					...option.grid,
					top: Math.max(Number(option.grid?.top) || 0, resolveTrendChartGridTop({ legendItemCount: legendData.length })),
				},
			};
		}
	}

	/** 多纵轴同比月/季：保留「年 · 指标」系列图例，并挂上该年合同金额/单数汇总 */
	if (hasVisibleLegend && hasCompositeSeriesName) {
		const suffixByName = buildCompositePeriodSummarySuffixByName(
			legendData,
			chartSummary.compareLabels,
			salesValues,
			orderValues,
		);
		const legend = mergeLegendConfig(
			existingLegend,
			buildCompareLineLegend(chartTheme, {
				data: existingLegend.data ?? legendData,
				itemCount: legendData.length,
				...(suffixByName ? { suffixByName } : {}),
			}),
		);
		return {
			...option,
			legend,
			grid: {
				...option.grid,
				top: Math.max(Number(option.grid?.top) || 0, resolveTrendChartGridTop({ legendItemCount: legendData.length })),
			},
		};
	}

	if (chartSummary.compareLabels?.length >= 2) {
		return {
			...option,
			legend: buildComparePeriodSummaryLegend(chartTheme, chartSummary.compareLabels, salesValues, orderValues),
			grid: {
				...option.grid,
				top: Math.max(Number(option.grid?.top) || 0, resolveTrendChartGridTop({ legendItemCount: chartSummary.compareLabels.length })),
			},
		};
	}

	return {
		...option,
		legend: buildStandaloneSummaryLegend(chartTheme, salesValues[0], orderValues[0]),
		grid: {
			...option.grid,
			top: Math.max(Number(option.grid?.top) || 0, 36),
		},
	};
};

export const enrichMetricLegendWithSummary = (legend, chartTheme, salesTotal, orderCount) => {
	if (!legend || legend.show === false) return legend;
	const data = legend.data;
	if (!Array.isArray(data) || !data.length) return legend;
	if (!legendUsesMetricNames(data)) return legend;
	return mergeLegendConfig(
		legend,
		buildCompareLineLegend(chartTheme, {
			data,
			itemCount: data.length,
			suffixByName: buildMetricSummarySuffixByName(salesTotal, orderCount),
		}),
	);
};
